import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Fieldset, Form, Layout, Spinner } from '@vwfs-bronson/bronson-react';
import { Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import { NumericFormField } from '../../../../../components/FormFields/NumericFormField';
import { TextFormField } from '../../../../../components/FormFields/TextFormField';
import { departmentList } from '../../../../../resources/base/models/departmentList';
import { EnumCommercialVehicleTransform } from '../../../../../resources/base/models/enums';
import { VehicleType } from '../../../../../resources/base/models/vehicleTypes';
import { calculateQuote } from '../../../../../services/api/miles/miles-services';
import { roundNumber } from '../../../../../services/common/formatNew';
import { removeEmptyFromObject } from '../../../../../services/common/miscUtils';
import { RootState, store } from '../../../../../services/redux';
import { loadingActions } from '../../../../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../../../services/routing/navigate';
import { ConfigurationsSection, VehicleConfigData } from '../../../../../types/vehicleConfig';
import { AccessoriesModal } from '../../../../AccessoriesOptionsModal/AccessoriesOptionsModal';
import { CarOptionsModal } from '../../../../CarOptionsModal/CarOptionsModal';
import { DeliveryCostModal } from '../../../../DeliveryCostModal/DeliveryCostModal';
import { RegistrationCostModal } from '../../../../RegistrationCostModal/RegistrationCostModal';
import { uiDateToApiDate } from '../../../utils/formatDate';
import { CheckboxField, Datepicker, Dropdown } from '../../fieldset';
import { VehicleConfigurationFinancialSchema } from '../../validationSchema';

export const AdditionalConfigurations: FunctionComponent<{
  setAdditionalConfigurationSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  expandedSection: ConfigurationsSection | null;
  initialValue: VehicleConfigData;
  vehicleType: VehicleType | undefined;
  demoCarCheckbox: boolean;
  stepValues: VehicleConfigData | undefined;
  rimOptions: VehicleIntegration.Components.Schemas.ModelOptions[];
  setExpandedSection: React.Dispatch<React.SetStateAction<ConfigurationsSection | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  formState: any;
  setFiscalPower: React.Dispatch<React.SetStateAction<number | undefined>>;
  isLoading: boolean;
  fiscalPower: number | undefined;
  prefillData?: QuoteSchema.Schemas.QuoteResponse;
  quoteId?: string;
  isFormReadonly?: boolean;
}> = ({
  setAdditionalConfigurationSubmitted,
  expandedSection,
  initialValue,
  vehicleType,
  demoCarCheckbox,
  setExpandedSection,
  setIsLoading,
  formState,
  rimOptions,
  setFiscalPower,
  isLoading,
  fiscalPower,
  prefillData,
  isFormReadonly = false,
}) => {
  const [carOptionsVisible, setCarOptionsVisible] = useState(false);
  const [accessoiresOptionsVisible, setAccessoiresOptionsVisible] = useState(false);
  const [deliveryCostVisible, setDeliveryCostVisible] = useState(false);
  const [regCostVisible, setRegCostVisible] = useState(false);
  const [accessoriesValue, setAccessoriesValue] = useState<number>(initialValue?.accessoires || 0);
  const [regCost, setRegCost] = useState<number>(initialValue?.totalRegCost || 0);
  const [optionsValue, setOptionsValue] = useState<number>(initialValue?.options || 0);
  const [resetPopupValues, setResetPopupValues] = useState<boolean>(false);
  const navigate = useNavigate();
  const quote = useSelector(quoteSelectors.selectQuote);
  const { values: stepValues, setValues: setStepValues, setFieldTouched } = useFormikContext<VehicleConfigData>();

  const isCarUsed = useSelector((state: RootState) => state.custom.isCarUsed);
  const quoteId = useSelector(quoteSelectors.selectQuoteId);
  const newCopiedQuoteSubmitted = useSelector(quoteSelectors.selectIsNewCopiedQuoteSubmitted);
  const isCarUsedOrDemo = isCarUsed || demoCarCheckbox;

  const handleCarOptions = (values: any) => {
    const options = [
      {
        prTypeId: '250001', // commercial vehicle transformation option enum value
        isSelected: values.transformationSoc,
      },
    ] as any[];

    rimOptions.forEach((rim) => {
      options.push({
        prTypeId: rim.prTypeId,
        isSelected: values.rims === rim.prTypeId,
      });
    });

    if (vehicleType === VehicleType.NEW && values.freeOptionsBTO) {
      options.push({
        isSelected: true,
        optionPriceAmount: {
          amount: roundNumber(values.freeOptionsBTO / 1.2), // calculating net amount from gross assuming 20% VAT
        },
        prTypeId: '400003',
      });
    }
    return options;
  };

  return (
    <Formik
      initialValues={initialValue}
      enableReinitialize
      validationSchema={VehicleConfigurationFinancialSchema(demoCarCheckbox, expandedSection, vehicleType)}
      onSubmit={async (values) => {
        const options = optionsValue;
        const accessoires = accessoriesValue;
        const totalRegCost = regCost;

        const finalData = {
          ...values,
          ...stepValues,
          options,
          accessoires,
          totalRegCost,
        };

        store.dispatch(loadingActions.setLoading(true));

        const _values = removeEmptyFromObject(values);

        try {
          if (!quoteId) {
            throw new Error('Missing quote Id!');
          }

          const request: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
            vehicleData: {
              ...(_values.fiscalPower !== undefined && { fiscalPower: _values.fiscalPower }),
              ...(vehicleType === VehicleType.USED && {
                milesDealerVehiclePrice: {
                  grossAmount: _values.catalogPrice,
                },
              }),
              model: {
                options: handleCarOptions(_values),
                ...(_values.dinHp !== undefined && {
                  engine: {
                    powerDetails: {
                      powerValues: [{ unit: 'HORSEPOWER', value: _values.dinHp }],
                    },
                  },
                }),
              },
            },
            financialProduct: {
              offers: [
                {
                  calculation: {
                    includingRegistration: _values.regCost,
                    commercialVehicleTransform: _values?.resolution
                      ? EnumCommercialVehicleTransform.restore
                      : EnumCommercialVehicleTransform.noRestore,
                  },
                  carToRestitute: Boolean(_values?.resolution),
                },
              ],
              contracts: [
                {
                  deliveryData: {
                    deliveryDate: _values?.estDelivery && uiDateToApiDate(_values?.estDelivery),
                  },
                },
              ],
            },
          };

          await calculateQuote(quoteId, request);
          setStepValues({ ...stepValues, ...finalData } as VehicleConfigData);
          setAdditionalConfigurationSubmitted(true);
          setExpandedSection(null);
          setIsLoading(false);
        } catch {
          navigate.error();
        } finally {
          store.dispatch(loadingActions.setLoading(false));
        }
      }}
    >
      {(formProps) => {
        const { values, handleSubmit, setFieldValue, resetForm } = formProps;
        const registrationCost = values?.regCost;
        const rimSelected = values?.isRimSelected;
        const transformationSociety = values?.transformationSoc;
        formState.current = {
          values,
          isValid: formProps.isValid,
          dirty: formProps.dirty,
        };

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const prevValuesRef = useRef<{ quoteId?: string; vehicleType?: VehicleType }>({
          quoteId,
          vehicleType: values.vehicleType,
        });

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          const prev = prevValuesRef.current;
          const hasChanged = prev.quoteId !== quoteId || prev.vehicleType !== vehicleType;

          if (!hasChanged) return;

          prevValuesRef.current = { quoteId, vehicleType };

          if (!newCopiedQuoteSubmitted) {
            setResetPopupValues(true);
          }

          const fieldsToBeSaved = [
            'estDelivery',
            'accessoires',
            'deliveryCost',
            'resolution',
            'transformationSoc',
            'catalogPrice',
          ];

          const keptValues = newCopiedQuoteSubmitted
            ? Object.fromEntries(fieldsToBeSaved.map((k) => [k, formState.current.values[k]]))
            : {};

          // fully reset fields after new quote is copied
          const resetToZero = newCopiedQuoteSubmitted
            ? { department: undefined, regCost: false, totalRegCost: 0, options: 0, freeOptionsBTO: undefined }
            : {};

          resetForm({
            values: {
              ...initialValue,
              ...keptValues,
              ...resetToZero,
            },
          });
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [quoteId, vehicleType]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (vehicleType === VehicleType.NEW) return;
          const newFiscalPower = quote?.vehicleData?.fiscalPower ?? fiscalPower;
          if (newFiscalPower) {
            setFieldValue('fiscalPower', newFiscalPower);
          }

          const newDinHp =
            quote?.vehicleData?.model?.engine?.powerDetails?.powerValues?.find((pw) => pw.unit === 'HORSEPOWER')
              ?.value ?? 0;
          if (newDinHp) {
            setFieldValue('dinHp', newDinHp);
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [quoteId]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (values.fiscalPower === undefined) return;
          setFiscalPower(values.fiscalPower);
        }, [values.fiscalPower]);

        return (
          <Form onSubmit={handleSubmit} className="u-pt-none">
            <Fieldset>
              {vehicleType === VehicleType.USED && (
                <>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        <Datepicker
                          name="estDelivery"
                          label={t('step1:configVehicle:estDelivery')}
                          infoText={t('step1:dateInfoText:label')}
                          value={values.estDelivery}
                          setFieldValue={setFieldValue}
                          minDate={new Date().setHours(0, 0, 0, 0)}
                          disabled={isFormReadonly}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/3" s="1/1">
                        <NumericFormField
                          fieldName="fiscalPower"
                          labelTranslationKey={t('step1:configVehicle:fiscalPower')}
                          type="cv"
                          minValue={1}
                          maximumFractionDigits={0}
                          isDisabled={isFormReadonly}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/3" s="1/1">
                        <NumericFormField
                          fieldName="dinHp"
                          labelTranslationKey={t('step1:configVehicle:dinHp')}
                          type="numeric"
                          minValue={1}
                          maximumFractionDigits={0}
                          isDisabled={isFormReadonly}
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/4" s="1/1">
                        <NumericFormField
                          fieldName="catalogPrice"
                          labelTranslationKey={t('step1:configVehicle:catalogPriceUsed')}
                          type="money"
                          isDisabled={isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/4" s="3/4" className="u-ml u-ml-none@m">
                        <NumericFormField
                          fieldName="accessoires"
                          labelTranslationKey={t('step1:configVehicle:accessoires')}
                          type="money"
                          isReadonly={!isFormReadonly}
                          isDisabled={isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>

                      <Layout.Item default="1/12" s="1/4" className="u-mt">
                        <Button
                          ariaLabel="add"
                          icon="semantic-plus"
                          secondary
                          onClick={() => {
                            setAccessoiresOptionsVisible(true);
                          }}
                          type="button"
                        />
                      </Layout.Item>
                      <Layout.Item default="1/4" s="3/4" className="u-ml u-ml-none@m">
                        <NumericFormField
                          fieldName="deliveryCost"
                          labelTranslationKey={t('step1:configVehicle:deliveryCost')}
                          type="money"
                          isReadonly={!isFormReadonly}
                          isDisabled={isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/12" s="1/4" className="u-mt">
                        <Button
                          ariaLabel="add"
                          icon="semantic-plus"
                          secondary
                          onClick={() => {
                            setDeliveryCostVisible(true);
                          }}
                          type="button"
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Layout>
                    {rimOptions.length > 0 && (
                      <Layout.Item default="1/4" s="1/1">
                        <CheckboxField
                          label={t('step1:configVehicle:rimsCheckbox')}
                          name="isRimSelected"
                          notRequired
                          setFieldValue={setFieldValue}
                          onValueChange={() => {
                            setFieldValue('rims', '');
                            setFieldTouched('rims', false);
                          }}
                          value={values.isRimSelected}
                          disabled={isFormReadonly}
                        />
                      </Layout.Item>
                    )}
                    {rimSelected && (
                      <Layout.Item default="1/4" s="1/1">
                        <Dropdown
                          name="rims"
                          label={t('step1:configVehicle:rims')}
                          list={rimOptions.map((rim) => ({
                            label: `${rim.prKey} - ${rim.name}`,
                            value: rim.prTypeId ?? '',
                          }))}
                          disabled={isFormReadonly}
                        />
                      </Layout.Item>
                    )}
                  </Layout>
                </>
              )}
              {vehicleType === VehicleType.STOCK && (
                <>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        <Datepicker
                          name="estDelivery"
                          label={t('step1:configVehicle:estDelivery')}
                          infoText={t('step1:dateInfoText:label')}
                          notRequired
                          value={values.estDelivery}
                          setFieldValue={setFieldValue}
                          minDate={new Date().setHours(0, 0, 0, 0)}
                          disabled={isFormReadonly}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/3" s="1/1">
                        <NumericFormField
                          fieldName="fiscalPower"
                          labelTranslationKey={t('step1:configVehicle:fiscalPower')}
                          type="cv"
                          minValue={1}
                          maximumFractionDigits={0}
                          isDisabled={isFormReadonly}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/3" s="1/1">
                        <NumericFormField
                          fieldName="dinHp"
                          labelTranslationKey={t('step1:configVehicle:dinHp')}
                          type="numeric"
                          minValue={1}
                          maximumFractionDigits={0}
                          isDisabled={isFormReadonly}
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/4" s="3/4">
                        <NumericFormField
                          fieldName="options"
                          labelTranslationKey={t('step1:configVehicle:options')}
                          type="money"
                          isReadonly={!isFormReadonly}
                          isDisabled={isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/12" s="1/4" className="u-mt">
                        <Button
                          ariaLabel="add"
                          icon="semantic-plus"
                          secondary
                          onClick={() => setCarOptionsVisible(true)}
                          type="button"
                        />
                      </Layout.Item>
                      <Layout.Item default="1/4" s="3/4">
                        <NumericFormField
                          fieldName="accessoires"
                          labelTranslationKey={t('step1:configVehicle:accessoires')}
                          type="money"
                          isDisabled={isFormReadonly}
                          isReadonly={!isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/12" s="1/4" className="u-mt">
                        <Button
                          ariaLabel="add"
                          icon="semantic-plus"
                          secondary
                          onClick={() => setAccessoiresOptionsVisible(true)}
                          type="button"
                        />
                      </Layout.Item>
                      <Layout.Item default="1/4" s="3/4">
                        <NumericFormField
                          fieldName="deliveryCost"
                          labelTranslationKey={t('step1:configVehicle:deliveryCost')}
                          type="money"
                          isReadonly={!isFormReadonly}
                          isDisabled={isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/12" s="1/4" className="u-mt">
                        <Button
                          ariaLabel="add"
                          icon="semantic-plus"
                          secondary
                          onClick={() => {
                            setDeliveryCostVisible(true);
                          }}
                          type="button"
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                </>
              )}
              {vehicleType === VehicleType.NEW && (
                <>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        <Datepicker
                          name="estDelivery"
                          label={t('step1:configVehicle:estDelivery')}
                          infoText={t('step1:dateInfoText:label')}
                          notRequired
                          value={values.estDelivery}
                          setFieldValue={setFieldValue}
                          minDate={new Date().setHours(0, 0, 0, 0)}
                          disabled={isFormReadonly}
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/4" s="3/4">
                        <NumericFormField
                          fieldName="accessoires"
                          labelTranslationKey={t('step1:configVehicle:accessoires')}
                          type="money"
                          isReadonly={!isFormReadonly}
                          isDisabled={isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/12" s="1/4" className="u-mt">
                        <Button
                          ariaLabel="add"
                          icon="semantic-plus"
                          secondary
                          onClick={() => {
                            setAccessoiresOptionsVisible(true);
                          }}
                          type="button"
                        />
                      </Layout.Item>
                      <Layout.Item default="1/4" s="3/4" className="u-ml u-ml-none@m">
                        <NumericFormField
                          fieldName="deliveryCost"
                          labelTranslationKey={t('step1:configVehicle:deliveryCost')}
                          type="money"
                          isReadonly={!isFormReadonly}
                          isDisabled={isFormReadonly}
                          maximumFractionDigits={2}
                        />
                      </Layout.Item>
                      <Layout.Item default="1/12" s="1/4" className="u-mt">
                        <Button
                          ariaLabel="add"
                          icon="semantic-plus"
                          secondary
                          onClick={() => {
                            setDeliveryCostVisible(true);
                          }}
                          type="button"
                        />
                      </Layout.Item>
                      <Layout.Item default="1/4" s="1/1" className="u-ml u-ml-none@m">
                        <NumericFormField
                          optional
                          fieldName="freeOptionsBTO"
                          labelTranslationKey={t('step1:configVehicle:freeOptionsBTO')}
                          type="money"
                          minValue={0}
                          maximumFractionDigits={2}
                          isDisabled={isFormReadonly}
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                </>
              )}
              <Layout>
                <Layout.Item default="1/4" s="1/1">
                  <CheckboxField
                    label={t('step1:configVehicle:regCost')}
                    name="regCost"
                    notRequired
                    value={values?.regCost}
                    setFieldValue={setFieldValue}
                    disabled={isFormReadonly}
                  />
                </Layout.Item>
                <Layout.Item default="2/3" s="1/1" className={`${rimOptions.length > 0 ? 'u-mt u-mt-none@s' : ''}`}>
                  {registrationCost && (
                    <Fieldset>
                      <Fieldset.Row>
                        <Layout>
                          <Layout.Item default="5/12" m="1/2" s="1/1">
                            <Dropdown
                              name="department"
                              label={t('step1:configVehicle:department')}
                              list={departmentList}
                              disabled={isFormReadonly}
                            />
                          </Layout.Item>
                          <Layout.Item default="1/3" m="2/5" s="3/4" className="u-ml u-ml-none@m">
                            <NumericFormField
                              fieldName="totalRegCost"
                              labelTranslationKey={t('step1:configVehicle:totalRegCost')}
                              type="money"
                              isReadonly={!isFormReadonly}
                              isDisabled={isFormReadonly || !values?.department}
                              maximumFractionDigits={2}
                            />
                          </Layout.Item>
                          <Layout.Item default="1/12" className="u-mt">
                            <Button
                              ariaLabel="add"
                              icon="semantic-plus"
                              secondary
                              onClick={() => {
                                setRegCostVisible(true);
                              }}
                              type="button"
                              disabled={!values.regCost || !values?.department}
                            />
                          </Layout.Item>
                        </Layout>
                      </Fieldset.Row>
                    </Fieldset>
                  )}
                </Layout.Item>
              </Layout>
              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/4" m="1/3" s="1/1" className={registrationCost ? 'u-mt' : ''}>
                    <CheckboxField
                      label={t('step1:configVehicle:transformationSoc')}
                      name="transformationSoc"
                      value={values.transformationSoc}
                      setFieldValue={setFieldValue}
                      disabled={isFormReadonly}
                    />
                  </Layout.Item>
                  <Layout.Item default="1/2" m="2/3" s="1/1" className="u-mt u-mt-none@s">
                    {transformationSociety && (
                      <Fieldset>
                        <Fieldset.Row>
                          <Layout>
                            <Layout.Item default="1/2" xs="1/1">
                              <TextFormField
                                fieldName="genreList"
                                labelTranslationKey="step1:configVehicle:genreList"
                                isReadonly
                                optional
                              />
                            </Layout.Item>

                            <Layout.Item default="1/2" xs="1/1" className="u-mt@s u-mt-none@xs">
                              <CheckboxField
                                label={t('step1:configVehicle:resolution')}
                                name="resolution"
                                disabled={!values.transformationSoc || isFormReadonly}
                                value={values.resolution}
                                setFieldValue={setFieldValue}
                              />
                            </Layout.Item>
                          </Layout>
                        </Fieldset.Row>
                      </Fieldset>
                    )}
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>
              <Layout right>
                <Layout.Item>
                  <Button
                    action
                    className="u-float-right"
                    id="calculatorButton"
                    testId="calculatorButton"
                    type="submit"
                    disabled={isFormReadonly}
                  >
                    {isLoading ? <Spinner section center small /> : t('step1:configVehicle:calculateButton')}
                  </Button>
                </Layout.Item>
              </Layout>
            </Fieldset>
            <CarOptionsModal
              isReadonly={isFormReadonly}
              visible={carOptionsVisible}
              resetPopupValues={resetPopupValues}
              setResetPopupValues={setResetPopupValues}
              prefillOptions={prefillData?.vehicleData?.model?.options}
              onCancel={(type) => {
                if (type !== 'close') {
                  setFieldValue('options', '');
                  setOptionsValue(0);
                }
                setCarOptionsVisible(false);
              }}
              onConfirm={(e) => {
                setCarOptionsVisible(false);
                setFieldValue('options', e);
                setOptionsValue(e);
              }}
              quoteId={quoteId}
            />
            <AccessoriesModal
              visible={accessoiresOptionsVisible}
              setResetPopupValues={setResetPopupValues}
              prefillOptions={prefillData?.vehicleData?.model?.options}
              resetPopupValues={resetPopupValues}
              isReadonly={isFormReadonly}
              onCancel={(type) => {
                if (type !== 'close') {
                  setAccessoriesValue(0);
                  setFieldValue('accessoires', '');
                }
                setAccessoiresOptionsVisible(false);
              }}
              onConfirm={(e) => {
                setAccessoiresOptionsVisible(false);
                setAccessoriesValue(e);
                setFieldValue('accessoires', e);
              }}
              estDelivery={values.estDelivery || undefined}
              catalogPrice={values.catalogPrice || undefined}
              quoteId={quoteId}
            />
            <RegistrationCostModal
              visible={regCostVisible}
              includingRegistration={Boolean(values.regCost)}
              isFormReadonly={isFormReadonly}
              department={values?.department}
              resetPopupValues={resetPopupValues}
              setResetPopupValues={setResetPopupValues}
              fuelType={formState.current.values?.engine}
              updateFiscalPower={(quote: QuoteSchema.Schemas.QuoteResponse) => {
                setFieldValue('fiscalPower', quote.vehicleData?.fiscalPower);
              }}
              onCancel={(type) => {
                if (type !== 'close') {
                  setFieldValue('totalRegCost', '');
                  setRegCost(0);
                }
                setRegCostVisible(false);
              }}
              onConfirm={(registrationCostsTotal) => {
                setRegCostVisible(false);
                setRegCost(registrationCostsTotal as number);
                setFieldValue('totalRegCost', registrationCostsTotal);
              }}
              fiscalPower={fiscalPower}
              estDelivery={values.estDelivery || undefined}
              catalogPrice={values.catalogPrice || undefined}
              quoteId={quoteId}
              prefillData={prefillData}
              isCarUsedOrDemo={isCarUsedOrDemo}
            />
            <DeliveryCostModal
              isReadonly={isFormReadonly}
              visible={deliveryCostVisible}
              setResetPopupValues={setResetPopupValues}
              resetPopupValues={resetPopupValues}
              onCancel={(type) => {
                if (type !== 'close') {
                  setFieldValue('deliveryCost', '');
                }
                setDeliveryCostVisible(false);
              }}
              onConfirm={(e) => {
                setDeliveryCostVisible(false);
                setFieldValue('deliveryCost', e);
              }}
              estDelivery={values.estDelivery || undefined}
              catalogPrice={values.catalogPrice || undefined}
              quoteId={quoteId}
              prefillOptions={prefillData?.financialProduct?.offers?.[0]?.deliveryOptions?.[0].deliveryCostComponents}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
